import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Search, FileDown, MessageSquare, Eye, Lock, Loader2, Sparkles, Check } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { ProductRatingBadge } from "@/components/products/ProductRatingBadge";
import { ReviewForm } from "@/components/products/ReviewForm";
import { ReviewsList } from "@/components/products/ReviewsList";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/hooks/useSubscription";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { AuthModal } from "@/components/AuthModal";
import { getStripeEnvironment } from "@/lib/stripe";
import type { User } from "@supabase/supabase-js";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface DigitalProduct {
  id: string;
  title: string;
  description: string;
  category: string;
  file_url: string | null;
  preview_image: string | null;
  file_size: string | null;
  file_format: string | null;
  download_count: number;
  tags: string[] | null;
  created_at: string;
  pdf_file_path: string | null;
  content_type: string;
}

interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
}

interface RatingStats {
  product_id: string;
  average_rating: number;
  total_reviews: number;
}

const PRODUCTS_PER_PAGE = 12;

const PremiumResources = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<DigitalProduct[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [ratingStats, setRatingStats] = useState<Map<string, RatingStats>>(new Map());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState<DigitalProduct | null>(null);
  const [reviewRefreshTrigger, setReviewRefreshTrigger] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState<User | null>(null);
  const [unlockedIds, setUnlockedIds] = useState<Set<string>>(new Set());
  const [unlocksLoading, setUnlocksLoading] = useState(true);
  const [unlockProduct, setUnlockProduct] = useState<DigitalProduct | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingUnlockProduct, setPendingUnlockProduct] = useState<DigitalProduct | null>(null);
  const { isActive: isSubscriber, loading: subLoading } = useSubscription(user);
  const { openCheckout, checkoutDialog } = useStripeCheckout();

  useEffect(() => {
    fetchData();
  }, []);

  // Track auth + per-product unlocks
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      const u = s?.user ?? null;
      setUser(u);
      if (u && pendingUnlockProduct) {
        setUnlockProduct(pendingUnlockProduct);
        setPendingUnlockProduct(null);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUnlockProduct]);

  useEffect(() => {
    if (!user) { setUnlockedIds(new Set()); setUnlocksLoading(false); return; }
    setUnlocksLoading(true);
    (async () => {
      const { data } = await supabase
        .from("purchases" as any)
        .select("entity_id")
        .eq("user_id", user.id)
        .eq("purchase_type", "digital_resource")
        .eq("environment", getStripeEnvironment());
      setUnlockedIds(new Set(((data as any[]) || []).map((r) => r.entity_id)));
      setUnlocksLoading(false);
    })();
  }, [user?.id]);

  // Only show loading state when a signed-in user's unlock status is still being resolved.
  const accessChecking = !!user && (unlocksLoading || subLoading);
  const isUnlocked = (productId: string) => isSubscriber || unlockedIds.has(productId);

  const fetchData = async () => {
    try {
      // Fetch products, categories, and rating stats in parallel
      const [productsRes, categoriesRes, statsRes] = await Promise.all([
        supabase
          .from("digital_products")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false }),
        supabase
          .from("product_categories")
          .select("*")
          .eq("is_active", true)
          .order("name"),
        supabase
          .from("product_review_stats")
          .select("product_id, average_rating, total_reviews"),
      ]);

      if (productsRes.error) throw productsRes.error;
      if (categoriesRes.error) throw categoriesRes.error;

      setProducts(productsRes.data || []);
      
      // Add "All Products" as first option
      const allCategories = [
        { id: "all", name: "All Products", slug: "all", icon: "🌟", color: "hsl(var(--primary))" },
        ...(categoriesRes.data || []),
      ];
      setCategories(allCategories);

      // Create rating stats map for O(1) lookup
      const statsMap = new Map<string, RatingStats>();
      statsRes.data?.forEach((stat) => {
        if (stat.product_id) {
          statsMap.set(stat.product_id, {
            product_id: stat.product_id,
            average_rating: stat.average_rating || 0,
            total_reviews: stat.total_reviews || 0,
          });
        }
      });
      setRatingStats(statsMap);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to load products. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter products with memoization
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (selectedCategory !== "all") {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [products, selectedCategory, searchQuery]);

  // Paginated products
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const requireUnlock = (product: DigitalProduct) => {
    if (!user) {
      setPendingUnlockProduct(product);
      setAuthOpen(true);
      return false;
    }
    if (!isUnlocked(product.id)) {
      setUnlockProduct(product);
      return false;
    }
    return true;
  };

  const handleDownload = async (product: DigitalProduct) => {
    if (!product.file_url) return;
    if (!requireUnlock(product)) return;
    try {
      await supabase.from("product_downloads").insert({ product_id: product.id, user_agent: navigator.userAgent });
      await supabase.from("digital_products").update({ download_count: product.download_count + 1 }).eq("id", product.id);
      window.open(product.file_url, "_blank");
      toast({ title: "Download Started", description: `${product.title} is downloading...` });
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, download_count: p.download_count + 1 } : p));
    } catch (error) {
      console.error("Error downloading:", error);
      toast({ title: "Download Failed", description: "Please try again later.", variant: "destructive" });
    }
  };

  const handleView = (product: DigitalProduct) => {
    if (!requireUnlock(product)) return;
    navigate(`/resource/${product.id}/view`);
  };

  const buyThisOnly = () => {
    if (!unlockProduct) return;
    const product = unlockProduct;
    setUnlockProduct(null);
    openCheckout({
      priceId: "digital_resource_unlock_v2",
      purchaseType: "digital_resource",
      entityId: product.id,
      title: `Unlock: ${product.title}`,
    });
  };

  const buyAllAccess = () => {
    setUnlockProduct(null);
    openCheckout({ priceId: "all_access_monthly", title: "Subscribe to All-Access" });
  };

  const getCategoryColor = (categorySlug: string) => {
    return categories.find((c) => c.slug === categorySlug)?.color || 'hsl(var(--primary))';
  };

  return (
    <>
      <Helmet>
        <title>Premium Digital Resources - Free Downloads | RunoBlog</title>
        <meta
          name="description"
          content="Access our library of premium digital products including PDFs, graphics, templates, audio, video, and more. 100% free downloads with no sign-up required."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />
        
        <div className="bg-gradient-to-br from-background via-background to-primary/5">
          {/* Hero Section */}
          <section className="py-20 px-4 text-center border-b border-border/50">
            <div className="container mx-auto max-w-5xl">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-primary/10 rounded-full">
              <FileDown className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-primary">100% Free • No Sign-up Required</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
              Premium Digital Resources
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Access our curated library of professional digital products. From templates and graphics to audio and video assets – all completely free.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Search products, templates, graphics..."
                className="pl-12 h-14 text-lg bg-card/50 backdrop-blur"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Category Pills */}
        <section className="py-6 px-4 border-b border-border/50 bg-card/30 backdrop-blur">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((category) => {
                const count = products.filter(p => 
                  category.slug === "all" ? true : p.category === category.slug
                ).length;
                return (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.slug ? "default" : "ghost"}
                    size="sm"
                    className={cn(
                      "gap-1.5 rounded-full px-4 transition-all",
                      selectedCategory !== category.slug && "hover:bg-muted",
                      count === 0 && "opacity-50"
                    )}
                    onClick={() => setSelectedCategory(category.slug)}
                  >
                    <span className="text-base">{category.icon}</span>
                    <span className="text-sm">{category.name}</span>
                    <Badge 
                      variant={selectedCategory === category.slug ? "secondary" : "outline"} 
                      className="ml-1 text-xs h-5 min-w-[20px] px-1.5"
                    >
                      {count}
                    </Badge>
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-7xl">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4 space-y-3">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-20">
                <FileDown className="w-20 h-20 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold mb-2">No Products Found</h3>
                <p className="text-muted-foreground">
                  {searchQuery || selectedCategory !== "all"
                    ? "Try adjusting your search or filters"
                    : "Products coming soon!"}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6 flex items-center justify-between">
                  <p className="text-muted-foreground">
                    Showing <span className="font-semibold text-foreground">{paginatedProducts.length}</span> of{" "}
                    <span className="font-semibold text-foreground">{filteredProducts.length}</span>{" "}
                    {filteredProducts.length === 1 ? "product" : "products"}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {paginatedProducts.map((product) => (
                    <Card
                      key={product.id}
                      className="group overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    >
                      {/* Preview Image */}
                      <div
                        className="h-48 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center relative overflow-hidden"
                        style={{
                          backgroundColor: product.preview_image ? 'transparent' : getCategoryColor(product.category),
                        }}
                      >
                        {product.preview_image ? (
                          <img
                            src={product.preview_image}
                            alt={product.title}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <span className="text-6xl opacity-50">
                            {categories.find((c) => c.slug === product.category)?.icon || "📦"}
                          </span>
                        )}
                      </div>

                      <CardContent className="p-4 space-y-3">
                        {/* Title */}
                        <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-primary transition-colors">
                          {product.title}
                        </h3>

                        {/* Description */}
                        <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>

                        {/* Meta Info */}
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <Badge variant="secondary" className="gap-1">
                            <span>
                              {categories.find((c) => c.slug === product.category)?.icon}
                            </span>
                            {categories.find((c) => c.slug === product.category)?.name}
                          </Badge>
                          {product.file_size && (
                            <span className="flex items-center gap-1">
                              📦 {product.file_size}
                            </span>
                          )}
                          {product.file_format && (
                            <span className="flex items-center gap-1">
                              📄 {product.file_format}
                            </span>
                          )}
                        </div>

                        {/* Download Stats */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t border-border/50">
                          <Download className="w-4 h-4" />
                          <span>{product.download_count.toLocaleString()} Get</span>
                        </div>

                        {/* Rating Badge - now uses pre-fetched stats */}
                        <ProductRatingBadge stats={ratingStats.get(product.id)} />

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => setSelectedProduct(product)}
                              >
                                <MessageSquare className="h-4 w-4 mr-1" />
                                Reviews
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>{product.title} - Reviews</DialogTitle>
                              </DialogHeader>
                              <Tabs defaultValue="reviews" className="mt-4">
                                <TabsList className="grid w-full grid-cols-2">
                                  <TabsTrigger value="reviews">All Reviews</TabsTrigger>
                                  <TabsTrigger value="write">Write Review</TabsTrigger>
                                </TabsList>
                                <TabsContent value="reviews" className="mt-4">
                                  <ReviewsList 
                                    productId={product.id} 
                                    refreshTrigger={reviewRefreshTrigger}
                                  />
                                </TabsContent>
                                <TabsContent value="write" className="mt-4">
                                  <ReviewForm
                                    productId={product.id}
                                    onReviewSubmitted={() => {
                                      setReviewRefreshTrigger(prev => prev + 1);
                                    }}
                                  />
                                </TabsContent>
                              </Tabs>
                            </DialogContent>
                          </Dialog>

                          {product.content_type === "viewable" && product.pdf_file_path ? (
                            <Button
                              className="flex-1 gap-2 group-hover:shadow-lg"
                              onClick={() => handleView(product)}
                              size="sm"
                              variant={accessChecking ? "outline" : isUnlocked(product.id) ? "default" : "secondary"}
                              disabled={accessChecking}
                              aria-busy={accessChecking}
                            >
                              {accessChecking ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Checking...
                                </>
                              ) : isUnlocked(product.id) ? (
                                <>
                                  <Eye className="w-4 h-4" />
                                  View
                                </>
                              ) : (
                                <>
                                  <Lock className="w-4 h-4" />
                                  Unlock $3
                                </>
                              )}
                            </Button>
                          ) : product.file_url ? (
                            <Button
                              className="flex-1 gap-2 group-hover:shadow-lg"
                              onClick={() => handleDownload(product)}
                              size="sm"
                              variant={accessChecking ? "outline" : isUnlocked(product.id) ? "default" : "secondary"}
                              disabled={accessChecking}
                              aria-busy={accessChecking}
                            >
                              {accessChecking ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Checking...
                                </>
                              ) : isUnlocked(product.id) ? (
                                <>
                                  <Download className="w-4 h-4" />
                                  Download
                                </>
                              ) : (
                                <>
                                  <Lock className="w-4 h-4" />
                                  Unlock $3
                                </>
                              )}
                            </Button>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className={cn(
                              "cursor-pointer",
                              currentPage === 1 && "pointer-events-none opacity-50"
                            )}
                          />
                        </PaginationItem>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first, last, current, and adjacent pages
                            return page === 1 || 
                                   page === totalPages || 
                                   Math.abs(page - currentPage) <= 1;
                          })
                          .map((page, index, arr) => {
                            // Add ellipsis if there's a gap
                            const showEllipsisBefore = index > 0 && page - arr[index - 1] > 1;
                            return (
                              <span key={page} className="flex items-center">
                                {showEllipsisBefore && (
                                  <span className="px-2 text-muted-foreground">...</span>
                                )}
                                <PaginationItem>
                                  <PaginationLink
                                    onClick={() => setCurrentPage(page)}
                                    isActive={currentPage === page}
                                    className="cursor-pointer"
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              </span>
                            );
                          })}
                        
                        <PaginationItem>
                          <PaginationNext 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            className={cn(
                              "cursor-pointer",
                              currentPage === totalPages && "pointer-events-none opacity-50"
                            )}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
        </div>
        
        <Footer />
      </div>

      {/* Unlock choice dialog */}
      <Dialog open={!!unlockProduct} onOpenChange={(o) => !o && setUnlockProduct(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Unlock {unlockProduct?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Choose how you'd like access. Both options unlock instantly and add the resource to <strong>My Learning</strong>.
            </p>

            <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
              <div className="flex items-baseline justify-between mb-1">
                <h4 className="font-semibold">Buy this resource only</h4>
                <span className="text-xl font-bold">$3</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                One-time payment. Lifetime access to this single resource.
              </p>
              <Button className="w-full" onClick={buyThisOnly}>
                <Check className="mr-2 h-4 w-4" />
                Buy this resource — $3
              </Button>
            </div>

            <div className="rounded-lg border border-border p-4">
              <div className="flex items-baseline justify-between mb-1">
                <h4 className="font-semibold">Get Full Premium Access</h4>
                <span className="text-xl font-bold">$9.99<span className="text-xs font-normal text-muted-foreground">/mo</span></span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Unlock every premium resource, guide, and 28-day roadmap. Cancel anytime.
              </p>
              <Button variant="outline" className="w-full" onClick={buyAllAccess}>
                <Sparkles className="mr-2 h-4 w-4" />
                Get All-Access — $9.99/mo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      {checkoutDialog}
    </>
  );
};

export default PremiumResources;
